import { useMemo } from 'react';
import type Rollbar from 'rollbar';
import { useRollbarPerson } from '@rollbar/react';
import { useUser } from '@rhino-project/core/hooks';
import { useRhinoConfig } from '@rhino-project/core/config';

/**
 * Rollbar configuration derived from the Rhino environment. Enabled in
 * production builds or when ROLLBAR_ENABLED=true; uncaught errors and
 * unhandled promise rejections are captured by default. Pass `overrides`
 * to add or replace any config keys.
 */
export const useRollbarConfig = (
  overrides?: Partial<Rollbar.Configuration>
): Rollbar.Configuration => {
  const { env } = useRhinoConfig();

  return useMemo(
    () => ({
      accessToken: env.ROLLBAR_ACCESS_TOKEN,
      environment: env.ROLLBAR_ENV || process.env.NODE_ENV || 'development',
      enabled:
        process.env.NODE_ENV === 'production' ||
        String(env.ROLLBAR_ENABLED) === 'true',
      captureUncaught: true,
      captureUnhandledRejections: true,
      ...overrides
    }),
    [env, overrides]
  );
};

/**
 * Attach the signed-in user to Rollbar so events are attributed to a person,
 * clearing the person on sign-out. Call from a component rendered inside both
 * RhinoProvider (to read the user) and RollbarProvider (to reach Rollbar).
 */
export const useIdentifyRollbar = () => {
  const user = useUser();
  const person = useMemo(
    () =>
      user
        ? {
            id: user.id,
            email: user.email,
            username: user.name ?? user.nickname ?? undefined
          }
        : {},
    [user]
  );

  useRollbarPerson(person);
};
